/**
 * Created by piotr.pozniak@thebeaverhead.com on 15/12/2020.
 */

import React, { useCallback, useContext } from "react";
import classNames from "classnames";
import CardDateFormatter from "./CardDateFormatter";
import CardLocationFormatter from "./CardLocationFormatter";
import EventBadges from "../EventBadges";
import EventCTA from "../EventsList/EventCTA";
import CardEventHeader from "./CardEventHeader";
import PrivateEventBadge from "../PrivateEventBadge";
import CalendarLinkingContext from "../../../../contexts/CalendarLinkingContext";
import CardTags from "./CardTags";

function getContentBeforeFirstBr(html) {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const p = doc.querySelector("p");

  if (!p) {
    return html;
  }

  const br = p.querySelector("br");
  if (!br) {
    return p.innerHTML;
  }

  let result = "";
  for (const node of p.childNodes) {
    if (node === br) {
      break;
    }
    result += node.textContent ?? node.outerHTML ?? "";
  }

  return result;
}

/**
 * CardEvent component
 * @param props
 * @param {Object} props.event - event object
 * @param {String} props.inpicDatePlacement - placement of the date in the image
 * @param {function} props.onCardClick - callback on mouse up
 * @param {Boolean} props.showDate - show date
 * @param {Boolean} props.showTitle - show title
 * @param {Boolean} props.showLocation - show location
 * @param {Boolean} props.showDescription - show description
 * @param {String} props.descriptionClamp
 * @param {Boolean} props.showSummary - show summary
 * @param {Boolean} props.cCardCtaButtonFullWidth - tells whether the CTA button should be full width
 * @param {Boolean} props.embedded - tells whether the card is calendar is embedded. if not embedded, then it's in a builder
 * @param {String} [props.hoverEffect = "zoom"] - hover effect
 * @param {Boolean} [props.showWeekDay = false] - show week day
 * @param {String} [props.weekDayFormatter = ""] - week day formatter
 * @param {Boolean} props.useEventAccent - use event accent
 * @param {Boolean} props.showImage - show image
 * @param {String} props.coverAspectRatio - show image
 * @param {Boolean} props.showRecurringBadge - show recurring badge
 * @param {Boolean} props.showFeaturedBadge - show featured badge
 * @param {String} props.badgesLocation - location of the badges
 * @param {Boolean} props.showCardCta - show card CTA
 * @param {Boolean} props.showAsList - show as list
 * @param {Boolean} [props.showImageContainer = true] - show image container
 * @param {Boolean} props.cardCtaButtonWidth - card CTA button width
 * @param {Boolean} props.triggerCTAOnCardClick - trigger CTA on card click
 * @param {Boolean} props.showAccentOnCard - show accent on card
 * @param {Boolean} props.showTags - show tags on card
 * @param {Boolean} props.showTagsAsPills - show tags on card
 * @param {Array[string]} props.rejectedTags - show tags on card
 * @param {string|number} props.numberOfTags - number of tags to show
 * @param {Boolean} props.showTagsAsPills - list of tags to be rejected
 * @param {Boolean} props.areCardsEqualHeight - extra class that makes the card equal height
 * @returns {Element}
 * @constructor
 */
const CardEvent = ({
  hoverEffect = "zoom",
  showWeekDay = false,
  weekDayFormatter = "",
  showImageContainer = true,

  ...props
}) => {
  const { event, showCardCta, selectedDateTimeFormat } = props;

  const calendarLinking = useContext(CalendarLinkingContext);
  /**
   *
   * @param e
   */
  const onCardClick = useCallback(
    (e) => {
      if (props.onCardClick) {
        return props.onCardClick(e);
      }

      calendarLinking.onClickEvent(event)(e);
    },
    [props.onCardClick, calendarLinking.onClickEvent]
  );

  const banner =
    props.showImage && event.cover_image ? (
      <img src={event.cover_image} alt={event.name} title={event.name} />
    ) : null;

  const recurringBadges =
    (props.showRecurringBadge && event.recurrence_description) ||
    props.showFeaturedBadge ? (
      <EventBadges
        showRecurringBadge={
          props.showRecurringBadge && event.recurrence_description
            ? true
            : false
        }
        showFeaturedBadge={props.showFeaturedBadge && event.is_featured}
        location={props.badgesLocation}
      />
    ) : null;

  const description =
    props.descriptionClamp === "clamp_paragraph"
      ? getContentBeforeFirstBr(event.description)
      : event.description;

  const descriptionElement = props.showDescription ? (
    <p
      className={classNames(
        `card-view--card-content-body_${props.descriptionClamp}`
      )}
      dangerouslySetInnerHTML={{ __html: description }}
    ></p>
  ) : null;

  const summaryElement = props.showSummary ? (
    <p
      className="line-clamp"
      dangerouslySetInnerHTML={{ __html: event.summary }}
    ></p>
  ) : null;

  const cardCTAElement = showCardCta ? (
    <EventCTA
      event={event}
      settingsPrefix={"cCard"}
      className={classNames("dce--card-details-item dce--card--cta")}
      buttonClassName={classNames("dce--event-card--cta-btn", {
        "dce--event-card--cta-btn-auto-width": !props.cCardCtaButtonFullWidth,
      })}
    />
  ) : null;

  const title = props.showTitle ? <h3>{event.name}</h3> : null;

  const date = props.showDate ? (
    <CardDateFormatter
      startTime={event.start_time}
      endTime={event.end_time}
      showWeekDay={showWeekDay}
      weekDayFormatter={weekDayFormatter}
      allDayEvent={event.allDay}
      selectedDateTimeFormat={selectedDateTimeFormat}
    />
  ) : null;

  const location = props.showLocation ? (
    <CardLocationFormatter event={event} />
  ) : null;

  const inlineStyle = {};

  if (props.useEventAccent && event.accentColor) {
    inlineStyle.backgroundColor = event.accentColor;
  }

  const showBadgesInImage =
    (props.showRecurringBadge || props.showFeaturedBadge) &&
    ["image-top", "image-bottom"].indexOf(props.badgesLocation) >= 0;

  const tags = props.showTags ? (
    <CardTags
      event={event}
      showAsPills={props.showTagsAsPills}
      showTagsAsPills={props.showTagsAsPills}
      numberOfTags={props.numberOfTags}
      rejectedTags={props.rejectedTags}
      showTagSign={props.showTagSign}
    />
  ) : null;

  const cardContent =
    title ||
    date ||
    location ||
    descriptionElement ||
    summaryElement ||
    cardCTAElement ||
    props.showTags ||
    (recurringBadges && !showBadgesInImage) ? (
      <div
        className={classNames("card-view--card-content", {
          "dc-flex-1": props.badgesLocation === "title-bottom",
        })}
      >
        {recurringBadges && !showBadgesInImage ? recurringBadges : null}
        <div className={"card-view--card-content-body"}>
          {title}
          {date}
          {location}
          {descriptionElement}
          {summaryElement}
          {cardCTAElement}
          {tags}
        </div>
      </div>
    ) : (
      false
    );

  const cardHeader = showImageContainer ? (
    <CardEventHeader
      showBadgesInImage={showBadgesInImage}
      recurringBadges={recurringBadges}
      inlineStyle={inlineStyle}
      banner={banner}
      event={event}
      inpicDatePlacement={props.inpicDatePlacement}
      aspectRatio={props.coverAspectRatio}
    />
  ) : null;

  const privateEventBadge =
    event.is_private * 1 == 1 && !props.embedded ? <PrivateEventBadge /> : null;

  return (
    <div
      className={classNames(
        "card-view--card",
        "dc-flex-1",
        `card-view--card_hover-effect--${hoverEffect}`,
        {
          "card-view--card_cursor_pointer":
            props.triggerCTAOnCardClick || props.onSelectedEvent ? true : false,
          "dc-flex-col":
            props.badgesLocation === "title-bottom" && !props.showAsList,
          "card-view--card_equal-height": props.areCardsEqualHeight,
        }
      )}
    >
      <a
        className={"card-view--card-link"}
        aria-label={event.name}
        onClick={onCardClick}
        href={calendarLinking.getEventUrl(event)}
      />
      {cardHeader}
      {cardContent}
      {privateEventBadge}
      <div className={"card_hover-effect--accent-simple"} style={inlineStyle} />
      <div
        className={classNames({
          "card-view--card_permanent-accent": props.showAccentOnCard,
        })}
        style={inlineStyle}
      />
      <div
        className={"card_hover-effect--accent-expandable"}
        style={inlineStyle}
      />
    </div>
  );
};

export default CardEvent;
